import { useQuery } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { apiFetch } from './api.ts';
import { useSession } from './session.tsx';

/**
 * „Zuletzt geöffnet“ (Redesign OpenAI-Stil 31.10.2026, Idee aus Entwurf Variante C): die letzten geöffneten Datensätze
 * (Auftrag, Kunde, Artikel, Beleg) als kurze Liste in der breiten Seitenleiste.
 *
 * Story 26.9 ([RUDOLF] 01.11.2026): Der Browser merkt sich je Person nur Gruppe und Kennung (UUID) – keine Namen. Die
 * Anzeige-Namen lädt die Seitenleiste beim Anzeigen über `POST /api/v1/search/recent` (dieselben Rechte wie die Suche;
 * gelöschte und anonymisierte fehlen). Fehlt der Speicher (privates Fenster), bleibt die Liste einfach leer.
 */
export type RecentGroup = 'orders' | 'customers' | 'articles' | 'documents';

export interface RecentRef {
  group: RecentGroup;
  id: string;
}

/** Was die Seitenleiste zeigt: Adresse und Name vom Server. */
export interface RecentEntry {
  path: string;
  title: string;
}

export const RECENT_MAX = 5;

/** Pfadteil der Datensatz-Seite je Gruppe (Routen `/auftraege/$id`, `/kunden/$id`, `/artikel/$id`, `/belege/$documentId`). */
const SEGMENTS: Record<RecentGroup, string> = { orders: 'auftraege', customers: 'kunden', articles: 'artikel', documents: 'belege' };
const GROUPS = new Map(Object.entries(SEGMENTS).map(([group, segment]) => [segment, group as RecentGroup]));
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Datensatz-Seite = genau zwei Pfadteile: bekannte Seite und eine Kennung (UUID). Sonst `null`. */
export function recordRef(pathname: string): RecentRef | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length !== 2) return null;
  const group = GROUPS.get(parts[0]!);
  const id = parts[1]!;
  return group && UUID.test(id) ? { group, id: id.toLowerCase() } : null;
}

export function recordPath(ref: RecentRef): string {
  return `/${SEGMENTS[ref.group]}/${ref.id}`;
}

/** Neuen Eintrag vorne einreihen (gleicher Datensatz nur einmal), höchstens `RECENT_MAX`. Reine Funktion. */
export function pushRecent(list: readonly RecentRef[], ref: RecentRef): RecentRef[] {
  return [ref, ...list.filter((e) => !(e.group === ref.group && e.id === ref.id))].slice(0, RECENT_MAX);
}

/** Nur gültige `{ group, id }` übernehmen – alles andere (auch alte Einträge mit Namen) fällt weg. */
export function parseRecent(raw: string | null): RecentRef[] {
  let parsed: unknown;
  try {
    parsed = raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .flatMap((e: unknown) => {
      if (typeof e !== 'object' || e === null) return [];
      const { group, id } = e as Record<string, unknown>;
      if (typeof group !== 'string' || typeof id !== 'string' || !Object.hasOwn(SEGMENTS, group) || !UUID.test(id)) return [];
      return [{ group: group as RecentGroup, id: id.toLowerCase() }];
    })
    .slice(0, RECENT_MAX);
}

/** Frühere Speicherstände mit Namen (`till-erp.recent.<Person>` im localStorage bzw. sessionStorage) löschen. */
export function purgeNamedRecent(storage: Pick<Storage, 'length' | 'key' | 'removeItem'>): void {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key?.startsWith(LEGACY_PREFIX)) keys.push(key);
  }
  keys.forEach((k) => storage.removeItem(k));
}

const LEGACY_PREFIX = 'till-erp.recent.';
const storageKey = (userId: string) => `till-erp.recent-ids.${userId}`;

const listeners = new Set<() => void>();
let cache: { key: string; list: RecentRef[] } | null = null;
let purged = false;

function read(key: string): RecentRef[] {
  if (cache?.key === key) return cache.list;
  let list: RecentRef[];
  try {
    if (!purged) {
      purged = true;
      purgeNamedRecent(window.localStorage);
      purgeNamedRecent(window.sessionStorage);
    }
    list = parseRecent(window.localStorage.getItem(key));
  } catch {
    list = [];
  }
  cache = { key, list };
  return list;
}

function write(key: string, list: RecentRef[]): void {
  cache = { key, list };
  try {
    window.localStorage.setItem(key, JSON.stringify(list));
  } catch {
    // Ohne Speicher bleibt die Liste nur bis zum Neuladen.
  }
  listeners.forEach((l) => l());
}

/** Merkt sich die geöffnete Datensatz-Seite (nur Gruppe und Kennung). */
export function useTrackRecent(userId: string, pathname: string): void {
  useEffect(() => {
    const ref = recordRef(pathname);
    if (!ref) return;
    const key = storageKey(userId);
    const list = read(key);
    if (list[0]?.group === ref.group && list[0].id === ref.id) return;
    write(key, pushRecent(list, ref));
  }, [userId, pathname]);
}

function useRecentRefs(userId: string): RecentRef[] {
  const key = storageKey(userId);
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => read(key),
    () => [],
  );
}

/** Einträge mit Namen vom Server; während des Ladens und bei Fehlern leer (kein Platzhalter). */
export function useRecent(userId: string): RecentEntry[] {
  const refs = useRecentRefs(userId);
  const { getToken } = useSession();
  const query = useQuery({
    queryKey: ['recent-titles', userId, refs.map((r) => `${r.group}:${r.id}`).join(',')],
    queryFn: () => apiFetch<{ items: (RecentRef & { title: string })[] }>(getToken, '/api/v1/search/recent', { method: 'POST', body: { items: refs } }),
    enabled: refs.length > 0,
    // Namen sind Personendaten: nur kurz im Speicher der Seite halten.
    gcTime: 60_000,
    retry: false,
    // Kein Nachladen bei Rückkehr ins Fenster (sonst blendete die Leiste bei jedem Fokus kurz aus, siehe unten).
    refetchOnWindowFocus: false,
  });
  // Nur eine aktuelle, erfolgreiche Antwort zeigen: während des Ladens oder Nachladens (z. B. nach einer Anonymisierung)
  // und nach einem Fehler keine Namen aus dem Speicher der Seite stehen lassen.
  if (refs.length === 0 || query.isError || query.isFetching) return [];
  return (query.data?.items ?? []).map((item) => ({ path: recordPath(item), title: item.title }));
}
